import { useDisclosure } from "@msuite/ui/hooks";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { type CellContext, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import { Fragment, responseHandler } from "@msuite/ui/utils";
import type { Tables } from "@msuite/utils/types";
import { client } from "@/hono";
import { useCitadel } from "@/hooks/use-citadel";
import { EditTourWaveModal } from "@/modals/stock/edit-tour-wave-modal";

export function RowActions({ cell }: CellContext<Tables<"tourWaves">, "actions">) {
	/** Context */
	const { refetch } = useDataTableContext();

	/** Hooks */
	const { t } = useTranslation();
	const asyncModal = useAsyncModal();
	const disclosure = useDisclosure();

	/** Query */
	const { citadel } = useCitadel();

	/** Functions */
	async function handleOnDeleteTour() {
		const isConfirmed = await asyncModal.confirm();
		if (!isConfirmed) return;
		const response = await client.stock["tour-waves"][":tourWaveId"].$delete({
			param: { tourWaveId: cell.row.original.id },
		});
		await responseHandler(response);
		await refetch?.();
	}

	/** Computed */
	// The menu is an array of items, so the gate sits at the caller rather than in
	// a `<Can>` wrapper — same three-state guard as that component documents
	// (`apps/web/src/components/citadel/can.tsx`): dropped only on a definitive
	// denial, kept while the rules load so the server has the last word.
	const stockUpdateIsDenied = !!citadel && citadel.cannot("update", "Stock");
	const stockDeleteIsDenied = !!citadel && citadel.cannot("delete", "Stock");

	/** Render */
	return (
		<Fragment>
			<RowActionsWrapper to={null}>
				{(!stockUpdateIsDenied || !stockDeleteIsDenied) && (
					<Menu
						items={createStandardMenuItems({
							onEdit: stockUpdateIsDenied ? undefined : disclosure.onOpen,
							onDelete: stockDeleteIsDenied ? undefined : handleOnDeleteTour,
						})}
					>
						<Menu.Button />
					</Menu>
				)}
			</RowActionsWrapper>
			{disclosure.isOpen && (
				<EditTourWaveModal
					isOpen={disclosure.isOpen}
					onClose={disclosure.onClose}
					onComplete={refetch}
					tourWaveId={cell.row.original.id}
				/>
			)}
			{asyncModal.isOpen && (
				<ConfirmModal
					controller={asyncModal}
					message={t("stock.delete_tour_wave_confirmation")}
				/>
			)}
		</Fragment>
	);
}
